import { useMemo } from 'react';
import { t, Trans } from '@lingui/macro';
import { i18n } from '@lingui/core';
import { map } from 'lodash';
import { COLUMN_TYPES } from '../../../data-table/DataTableColumns';
import useRecordContext from '../../../../utilities/RecordContextProviderUtilities';
import InfrastructureTable from '../BaseInfrastructureTable/index';
import { RADIOS_URL } from './DeviceConstants';
import { INTERFACE_DELETED_FALSE_SOURCE } from '../InfrastructureConstants';

const EMPTY_STATE_MESSAGE = 'No radio available for this device';
const TABLE_ID = 'DeviceRadio';
const ROW_LIMIT = 100;

/**
 * Formats the band value to a human-readable frequency string
 * @param {number} value - Band identifier (1 = 2.4 GHz, other = 5 GHz)
 * @returns {string} Formatted band string
 */
const formatBand = (value) => (value === 1 ? '2.4 GHz' : '5 GHz');

/**
 * Formats the admin status value to a readable label
 * @param {number} value - Admin status (1 = Enabled, other = Disabled)
 * @returns {string} Formatted status string
 */
const formatAdminStatus = (value) => (value === 1 ? 'Enabled' : 'Disabled');

/**
 * Formats the BSSID list to a comma-separated string of SSIDs
 * @param {Array} value - Array of BSSID objects with ssid property
 * @returns {string} Comma-separated SSIDs or '--' if empty
 */
const formatSsids = (value) => map(value, 'ssid').join(', ') || '--';

/**
 * Read-only table displaying radio information for access point devices
 * Uses InfrastructureTable compound components with selector container
 * @returns {JSX.Element} InfrastructureTable configured for radio data
 */
const DeviceRadioTable = () => {
    const { id: deviceId } = useRecordContext();

    const fetchParams = useMemo(
        () => ({
            device: deviceId,
            rowLimit: ROW_LIMIT,
            ...INTERFACE_DELETED_FALSE_SOURCE,
        }),
        [deviceId]
    );

    const columns = useMemo(
        () => [
            {
                type: COLUMN_TYPES.TEXT,
                title: i18n._(t`Name`),
                mapping: 'name',
                width: 200,
                sortable: true,
            },
            {
                type: COLUMN_TYPES.TEXT,
                title: i18n._(t`Band`),
                mapping: 'band',
                width: 100,
                sortable: true,
                formatter: formatBand,
            },
            {
                type: COLUMN_TYPES.TEXT,
                title: i18n._(t`Status`),
                mapping: 'adminStatus',
                sortable: true,
                formatter: formatAdminStatus,
            },
            {
                type: COLUMN_TYPES.TEXT,
                title: i18n._(t`SSIDs`),
                mapping: 'bssidList',
                flex: 1,
                sortable: false,
                formatter: formatSsids,
            },
        ],
        []
    );

    return (
        <InfrastructureTable
            title={<Trans>Radios</Trans>}
            tableId={TABLE_ID}
            emptyStateMessage={EMPTY_STATE_MESSAGE}
            fetchUrl={RADIOS_URL}
            fetchParams={fetchParams}
            columns={columns}
        >
            <InfrastructureTable.SelectorContainer>
                <InfrastructureTable.Content />
            </InfrastructureTable.SelectorContainer>
        </InfrastructureTable>
    );
};

export default DeviceRadioTable;
